import { FC, useRef, useState, useEffect, useLayoutEffect } from 'react';
import Lottie from 'lottie-react';

import Typography from '@ixigo/iui/Typography';
import aiLoaderAnimation from '../../images/aiLoader.json';

const STEP_MS = 2000; // time each line stays before sliding
const TRANSITION_MS = 450; // slide animation duration

const SmartSupportLoader: FC = () => {
    const animationRef = useRef<any>(null);
    const itemRef = useRef<HTMLParagraphElement | null>(null);
    const [itemHeight, setItemHeight] = useState<number>(24); // fallback height
    const [index, setIndex] = useState(0);
    const [transitionOn, setTransitionOn] = useState(true);

    const loadingTexts = [
        'Please Wait',
        'Generating Summary',
        'Please wait',
        'Loading past threads',
        'This may take up to 30 seconds',
    ];

    // Duplicate first item at the end for seamless loop
    const tickerTexts = [...loadingTexts, loadingTexts[0]];

    // Measure line height to ensure precise translateY (works with any font/size)
    useLayoutEffect(() => {
        const measure = () => {
            if (itemRef.current) {
                const h = itemRef.current.getBoundingClientRect().height;
                if (h) setItemHeight(h);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Auto-advance index
    useEffect(() => {
        const id = setInterval(() => {
            setIndex((prev) => prev + 1);
        }, STEP_MS);
        return () => clearInterval(id);
    }, []);

    // When we slide to the duplicated last item, instantly jump back to 0 without animation
    const handleTransitionEnd = () => {
        if (index === loadingTexts.length) {
            // We've reached the duplicate (last slide). Snap back to real first.
            setTransitionOn(false);
            setIndex(0);

            // Re-enable transition on the next frame so future slides animate
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setTransitionOn(true));
            });
        }
    };

    return (
        <div className='flex flex-col min-h-screen bg-white'>
            <div className='flex-1 flex flex-col items-center justify-center px-6 py-12'>
                {/* Lottie Animation */}
                <Lottie
                    lottieRef={animationRef}
                    animationData={aiLoaderAnimation}
                    loop
                    autoplay
                    style={{ width: 70, height: 70 }}
                />

                {/* Vertical ticker */}
                <div className='text-center overflow-hidden mt-2' style={{ height: itemHeight }} aria-live='polite'>
                    <div
                        onTransitionEnd={handleTransitionEnd}
                        className='will-change-transform'
                        style={{
                            transform: `translateY(-${index * itemHeight}px)`,
                            transition: transitionOn ? `transform ${TRANSITION_MS}ms ease-in-out` : 'none',
                        }}
                    >
                        {tickerTexts.map((text) => (
                            <Typography
                                key={text}
                                ref={itemRef}
                                className='text-md font-sm leading-6'
                                style={{ height: itemHeight }}
                            >
                                {text}
                            </Typography>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default SmartSupportLoader;
